import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Collapse,
  TextField,
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as PlusIcon, 
  Filter as FilterIcon, 
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { TemplateEditDialog } from '@/components/sms/TemplateEditDialog';
import { RootState } from '@/store/store';
import { PageHeader } from '@/components/ui/PageHeader';
import { PageLayout } from '@/components/ui/PageLayout';
import {
  fetchTemplatesRequested,
  deleteTemplateRequested,
  setTemplateFilters,
  clearTemplateErrors,
} from '@/store/slices/smsTemplateSlice';
import { SmsTemplate } from '@/types/sms';

export default function SmsTemplateManagement() {
  const dispatch = useDispatch();
  const {
    templates,
    templatesLoading,
    templatesError,
    filters,
    deleteLoading,
  } = useSelector((state: RootState) => state.smsTemplate);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | null>(null);
  const [editMode, setEditMode] = useState<'create' | 'edit'>('create');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<SmsTemplate | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<keyof SmsTemplate>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  useEffect(() => {
    // Fetch templates on mount
    dispatch(fetchTemplatesRequested(filters));
  }, [dispatch]);

  const handleFilterChange = (field: string, value: any) => {
    const newFilters = { ...filters, [field]: value === 'all' ? undefined : value };
    dispatch(setTemplateFilters(newFilters));
    dispatch(fetchTemplatesRequested(newFilters));
  };

  const handleClearFilters = () => {
    const clearedFilters = {};
    dispatch(setTemplateFilters(clearedFilters));
    dispatch(fetchTemplatesRequested(clearedFilters));
  };

  // Count applied filters
  const getAppliedFiltersCount = (): number => {
    let count = 0;
    if (filters.lob_type) count++;
    if (filters.sms_type) count++;
    if (filters.is_active !== undefined) count++;
    if (filters.name) count++;
    return count;
  };

  // Get filter chips for display
  const getFilterChips = () => {
    const chips: { label: string; value: string }[] = [];
    
    if (filters.lob_type) {
      chips.push({ label: 'LOB', value: filters.lob_type });
    }
    if (filters.sms_type) {
      chips.push({ label: 'SMS Type', value: filters.sms_type === 'notice_only' ? 'Notice Only' : 'Notice + Link' });
    }
    if (filters.is_active !== undefined) {
      chips.push({ label: 'Status', value: filters.is_active ? 'Active' : 'Inactive' });
    }
    if (filters.name) {
      chips.push({ label: 'Name', value: filters.name });
    }
    
    return chips;
  };

  const appliedCount = getAppliedFiltersCount();
  const filterChips = getFilterChips();

  const handleCreateClick = useCallback(() => {
    setSelectedTemplate(null);
    setEditMode('create');
    setEditDialogOpen(true);
  }, []);

  const handleEditClick = useCallback((template: SmsTemplate) => {
    setSelectedTemplate(template);
    setEditMode('edit');
    setEditDialogOpen(true);
  }, []);

  const handleDeleteClick = useCallback((template: SmsTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      dispatch(deleteTemplateRequested(templateToDelete.id));
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleRefresh = () => {
    dispatch(clearTemplateErrors());
    dispatch(fetchTemplatesRequested(filters));
  };

  const getSmsTypeLabel = (smsType: string) => {
    return smsType === 'notice_only' ? 'Notice Only' : 'Notice + Link';
  };

  const calculateSegments = (content: string) => {
    const length = content.length;
    return length === 0 ? 0 : length <= 160 ? 1 : Math.ceil(length / 153);
  };

  const handleRequestSort = (property: keyof SmsTemplate) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getComparator = <Key extends keyof SmsTemplate>(
    order: 'asc' | 'desc',
    orderBy: Key
  ): (a: SmsTemplate, b: SmsTemplate) => number => {
    return order === 'desc'
      ? (a, b) => {
          if (orderBy === 'content') {
            return b.content.length - a.content.length;
          }
          if (b[orderBy] < a[orderBy]) return -1;
          if (b[orderBy] > a[orderBy]) return 1;
          return 0;
        }
      : (a, b) => {
          if (orderBy === 'content') {
            return a.content.length - b.content.length;
          }
          if (a[orderBy] < b[orderBy]) return -1;
          if (a[orderBy] > b[orderBy]) return 1;
          return 0;
        };
  };

  const sortedTemplates = React.useMemo(() => {
    return [...templates].sort(getComparator(order, orderBy));
  }, [templates, order, orderBy]);

  const paginatedTemplates = React.useMemo(() => {
    return sortedTemplates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedTemplates, page, rowsPerPage]);

  return (
    <PageLayout>
      <PageHeader
        icon={<DescriptionIcon />}
        title="SMS Template Management"
        description="Manage SMS templates for renewal notice communications"
        action={
          <Button 
            variant="contained" 
            onClick={handleCreateClick} 
            startIcon={<PlusIcon />}
            sx={(theme) => ({ backgroundColor: theme.palette.primary.main })}
          >
            Create Template
          </Button>
        }
      />

      {/* Filters */}
      <Card sx={{ marginBottom: 1.5 }}>
        <CardContent sx={{ p: filtersExpanded ? 1.5 : 1, '&:last-child': { pb: filtersExpanded ? 1.5 : 1 } }}>
          {/* Collapsed Header */}
          {!filtersExpanded && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: filterChips.length > 0 ? 1 : 0
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterIcon sx={(theme) => ({ width: '18px', height: '18px', color: theme.palette.primary.main })} />
                <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                  Filters {appliedCount > 0 && `(${appliedCount} applied)`}
                </Typography>
                {appliedCount > 0 && (
                  <Button
                    size="small"
                    onClick={handleClearFilters}
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    sx={{ ml: 0.5, py: 0.25 }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
              <IconButton
                onClick={() => setFiltersExpanded(true)}
                size="small"
                sx={{ p: 0.5 }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
          )}

          {/* Filter Chips (shown when collapsed) */}
          {!filtersExpanded && filterChips.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {filterChips.map((chip, index) => (
                <Chip
                  key={index}
                  label={`${chip.label}: ${chip.value}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ height: '24px', fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          )}

          {/* Expanded Header */}
          {filtersExpanded && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 1, 
              gap: 1.5, 
              flexWrap: 'wrap'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <FilterIcon sx={(theme) => ({ width: '18px', height: '18px', color: theme.palette.primary.main })} />
                <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0 }}>
                  Filters
                </Typography>
                <IconButton
                  onClick={() => setFiltersExpanded(false)}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  <ExpandLessIcon />
                </IconButton>
              </Box>
            
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5, 
                flexWrap: 'wrap',
                flexShrink: 0
              }}>
                {appliedCount > 0 && (
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    disabled={templatesLoading}
                    startIcon={<ClearIcon />}
                    size="small"
                    sx={{ py: 0.5 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Filter Form (collapsible) */}
          <Collapse in={filtersExpanded}>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              {/* Name Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Name"
                  placeholder="Search template name"
                  value={filters.name || ''}
                  onChange={(e) => handleFilterChange('name', e.target.value || undefined)}
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Line of Business */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Line of Business</InputLabel>
                  <Select
                    value={filters.lob_type || 'all'}
                    label="Line of Business"
                    onChange={(e) => handleFilterChange('lob_type', e.target.value)}
                  >
                    <MenuItem value="all">All LOBs</MenuItem>
                    <MenuItem value="MOTOR">MOTOR</MenuItem>
                    <MenuItem value="HEALTH">HEALTH</MenuItem>
                    <MenuItem value="ALL">ALL</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* SMS Type */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>SMS Type</InputLabel>
                  <Select
                    value={filters.sms_type || 'all'}
                    label="SMS Type"
                    onChange={(e) => handleFilterChange('sms_type', e.target.value)}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="notice_only">Notice Only</MenuItem>
                    <MenuItem value="notice_with_link">Notice + Link</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={
                      filters.is_active === undefined
                        ? 'all'
                        : filters.is_active
                        ? 'active'
                        : 'inactive'
                    }
                    label="Status"
                    onChange={(e) =>
                      handleFilterChange(
                        'is_active',
                        e.target.value === 'all' ? undefined : e.target.value === 'active'
                      )
                    }
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {templatesError && (
        <Alert 
          severity="error" 
          sx={{ marginBottom: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {templatesError}
        </Alert>
      )}

      {/* Templates Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', marginBottom: 1.5 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2 }}>
          {/* Left: Title and Count Chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
              Templates
            </Typography>
            <Chip 
              label={`${templates.length} template${templates.length !== 1 ? 's' : ''}`}
              color="primary"
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Table */}
        {templatesLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading templates...
            </Typography>
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">
              No templates found. Try adjusting your filters or create your first template.
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" onClick={handleCreateClick} startIcon={<PlusIcon />}>
                Create Template
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small" aria-label="templates table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'lob_type'}
                        direction={orderBy === 'lob_type' ? order : 'asc'}
                        onClick={() => handleRequestSort('lob_type')}
                      >
                        LOB
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'sms_type'}
                        direction={orderBy === 'sms_type' ? order : 'asc'}
                        onClick={() => handleRequestSort('sms_type')}
                      >
                        SMS Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                      Content Preview
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                      Variables
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'content'}
                        direction={orderBy === 'content' ? order : 'asc'}
                        onClick={() => handleRequestSort('content')}
                      >
                        Length
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'is_active'}
                        direction={orderBy === 'is_active' ? order : 'asc'}
                        onClick={() => handleRequestSort('is_active')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTemplates.map((template) => (
                    <TableRow
                      key={template.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      hover
                    >
                      <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                        {template.name}
                      </TableCell>
                      <TableCell>
                        <Chip label={template.lob_type} variant="outlined" size="small" />
                      </TableCell>
                      <TableCell>{getSmsTypeLabel(template.sms_type)}</TableCell>
                      <TableCell sx={{ maxWidth: 400 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.secondary',
                          }}
                        >
                          {template.content.substring(0, 80)}
                          {template.content.length > 80 && '...'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {template.variables.slice(0, 3).map((variable) => (
                            <Chip
                              key={variable}
                              label={variable}
                              size="small"
                              sx={(theme) => ({
                                fontSize: '0.75rem',
                                height: 20,
                                fontFamily: 'monospace',
                                backgroundColor: theme.palette.grey[100],
                              })}
                            />
                          ))}
                          {template.variables.length > 3 && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              +{template.variables.length - 3}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {template.content.length} chars
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {calculateSegments(template.content)} segment
                          {calculateSegments(template.content) !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={template.is_active ? 'Active' : 'Inactive'}
                          color={template.is_active ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(template)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(template)}
                          disabled={deleteLoading}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={templates.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </>
        )}
      </Paper>

      {/* Edit/Create Dialog */}
      <TemplateEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedTemplate(null);
          // Refresh templates after closing
          dispatch(fetchTemplatesRequested(filters));
        }}
        template={selectedTemplate}
        mode={editMode}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will deactivate the template "{templateToDelete?.name}". It will no
            longer be used for SMS sending but will remain in the system for reference.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Deactivate Template
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

